// client/src/components/company/BankInfo.js
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Info, AlertCircle } from 'lucide-react';
import { getBankData } from '../../config/banksData';

const BankInfo = () => {
  const { bankName } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // Stati per i modal
  const [showCet1Modal, setShowCet1Modal] = useState(false);
  const [showTcrModal, setShowTcrModal] = useState(false);

  // Recupera i dati della banca dal file di configurazione
  const decodedBankName = decodeURIComponent(bankName);
  const bankData = getBankData(decodedBankName, i18n.language); // Passa la lingua corrente

  // Se la banca non è configurata, mostra un messaggio di errore
  if (!bankData) {
    return (
      <div className="request-detail" style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div className="card">
          <div className="card-title">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
              <button 
                onClick={() => navigate(-1)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--primary)',
                  cursor: 'pointer',
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '6px',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = 'rgba(26, 115, 232, 0.1)'}
                onMouseLeave={(e) => e.target.style.background = 'transparent'}
              >
                <ArrowLeft size={24} />
              </button>
              <div style={{ fontSize: '20px', fontWeight: '600' }}>
                {decodedBankName}
              </div>
            </div>
          </div>
          
          <div className="alert-danger" style={{ margin: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <AlertCircle size={24} />
              <div>
                <strong>Informazioni non disponibili</strong>
                <p style={{ margin: '8px 0 0 0' }}>
                  I dati per la banca "{decodedBankName}" non sono ancora stati configurati nel sistema.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="request-detail" style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <div className="card">
        {/* Header con pulsante indietro */}
        <div className="card-title">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
            <button 
              onClick={() => navigate(-1)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--primary)',
                cursor: 'pointer',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                borderRadius: '6px',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = 'rgba(26, 115, 232, 0.1)'}
              onMouseLeave={(e) => e.target.style.background = 'transparent'}
            >
              <ArrowLeft size={24} />
            </button>
            <div>
              <div style={{ fontSize: '20px', fontWeight: '600' }}>
                {t('bankDetails.title', { bankName: bankData.name })}
              </div>
              <div style={{ fontSize: '13px', fontWeight: '400', opacity: '0.8', marginTop: '4px' }}>
                {t('bankDetails.dataAsOf', { date: bankData.dataDate })}
              </div>
            </div>
          </div>
        </div>

        {/* Descrizione */}
        <div className="request-info">
          <h3>{t('bankDetails.description')}</h3>
          <div className="notes-section">
            <div className="info-card-value">
              {bankData.description}
            </div>
          </div>
        </div>

        {/* Dati Principali */}
        <div className="request-info">
          <h3>{t('bankDetails.mainData')}</h3>
          <div className="info-grid">
            <div className="info-card">
              <div className="info-card-label">{t('bankDetails.listed')}</div>
              <div className="info-card-value">
                {bankData.listed ? t('common.yes') : t('common.no')}
              </div>
            </div>
            
            <div className="info-card" style={{ borderLeft: '4px solid var(--primary)' }}>
              <div className="info-card-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {t('bankDetails.cet1')}
                <Info 
                  size={16} 
                  style={{ color: 'var(--primary)', cursor: 'pointer' }} 
                  onClick={() => setShowCet1Modal(true)}
                  title={t('bankDetails.clickForInfo')}
                />
              </div>
              <div className="info-card-value" style={{ color: 'var(--primary)', fontSize: '18px' }}>
                {bankData.cet1}
              </div>
            </div>
            
            <div className="info-card" style={{ borderLeft: '4px solid var(--primary)' }}>
              <div className="info-card-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {t('bankDetails.tcr')}
                <Info 
                  size={16} 
                  style={{ color: 'var(--primary)', cursor: 'pointer' }} 
                  onClick={() => setShowTcrModal(true)}
                  title={t('bankDetails.clickForInfo')}
                />
              </div>
              <div className="info-card-value" style={{ color: 'var(--primary)', fontSize: '18px' }}>
                {bankData.tcr}
              </div>
            </div>
            
            <div className="info-card">
              <div className="info-card-label">{t('bankDetails.totalAssets')}</div>
              <div className="info-card-value">{bankData.totalAssets}</div>
            </div>
            
            <div className="info-card">
              <div className="info-card-label">{t('bankDetails.totalDeposits')}</div>
              <div className="info-card-value">{bankData.totalDeposits}</div>
            </div>
            
            <div className="info-card">
              <div className="info-card-label">{t('bankDetails.equity')}</div>
              <div className="info-card-value">{bankData.equity}</div>
            </div>
            
            <div className="info-card">
              <div className="info-card-label">{t('bankDetails.countryRating')}</div>
              <div className="info-card-value" style={{ fontSize: '14px' }}>
                {bankData.countryRating}
              </div>
            </div>
            
            <div className="info-card">
              <div className="info-card-label">{t('bankDetails.bankRating')}</div>
              <div className="info-card-value">
                {bankData.bankRating || t('bankDetails.notAvailable')}
              </div>
            </div>
          </div>
        </div>

        {/* Modal CET1 - Usando classi esistenti */}
        {showCet1Modal && (
          <div className="modal-overlay" onClick={() => setShowCet1Modal(false)}>
            <div className="modal" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {t('bankDetails.cet1Ratio')}
                  <span className="rate-type-badge fixed" style={{ textTransform: 'none' }}>CET1</span>
                </h3>
                <button 
                  className="close-btn"
                  onClick={() => setShowCet1Modal(false)}
                  aria-label="Chiudi"
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p style={{ margin: '0 0 16px 0', color: '#495057', lineHeight: '1.6' }}>
                  {t('bankDetails.cet1FullExplanation')}
                </p>
                <div style={{ 
                  background: '#f8f9fa', 
                  padding: '16px', 
                  borderRadius: '6px',
                  borderLeft: '4px solid var(--success)',
                  color: '#495057'
                }}>
                  <strong>{t('bankDetails.europeanMinimum')}:</strong> 8,0%
                  <br />
                  <strong>{t('bankDetails.italianBanksMinimum')}:</strong> 10,5%
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal TCR - Usando classi esistenti */}
        {showTcrModal && (
          <div className="modal-overlay" onClick={() => setShowTcrModal(false)}>
            <div className="modal" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {t('bankDetails.tcrRatio')}
                  <span className="rate-type-badge floating" style={{ textTransform: 'none', background: '#6c757d', color: 'white' }}>TCR</span>
                </h3>
                <button 
                  className="close-btn"
                  onClick={() => setShowTcrModal(false)}
                  aria-label="Chiudi"
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p style={{ margin: '0', color: '#495057', lineHeight: '1.6' }}>
                  {t('bankDetails.tcrFullExplanation')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BankInfo;